/**
 * Sharing for the whole map — `docs/property-maps-v2.md` §5.5, board 4.
 *
 * One row per layer with its audience and its pin, plus *Set every layer to*
 * and Apply. **This screen exists so nobody shares by accident**: it is the
 * one glance that answers "who sees what on this map", which the per-layer
 * dialog cannot because it only ever shows one layer.
 *
 * **Apply skips `Only me` layers and says so.** Somebody's scratch work
 * becoming visible to every crew because they used a bulk control is the one
 * mistake this screen must not make, which is why the server skips them too
 * (§4.1) rather than trusting the client to leave them out.
 */
import { useState } from 'react'
import type { Audience } from '@rowted/shared'
import { Button } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { SelectInput } from '../../components/form/inputs.js'
import { cn } from '../../lib/cn.js'
import { setMapAudience, type MapFeature, type MapLayer } from '../api.js'
import { layerWords, shapeWords } from '../words.js'
import { AudienceChip, type Division } from './audience-chip.js'

export function MapSharingDialog({
  mapId, mapName, layers, features, divisions, onClose, onChanged, onOpenLayer,
}: {
  mapId: string
  mapName: string
  layers: MapLayer[]
  features: MapFeature[]
  divisions: Division[]
  onClose: () => void
  onChanged: () => void
  onOpenLayer: (layer: MapLayer) => void
}) {
  const [all, setAll] = useState<Audience>('all')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  const scratch = layers.filter((one) => one.audience === 'me').length

  async function apply() {
    setBusy(true)
    try {
      const body = await setMapAudience(mapId, { audience: all, divisionIds: [] })
      setSaid(`${layerWords(body.changed.length)} changed`
        + (scratch > 0 ? ` · ${scratch} Only me ${scratch === 1 ? 'layer stays' : 'layers stay'}` : ''))
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SheetOrDialog
      onClose={onClose}
      title={`Sharing · ${mapName}`}
      width={560}
      footerNote="New layers start as All crews."
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-3 px-5 py-4">
        <div className="overflow-hidden rounded-field border border-line">
          {layers.map((layer) => (
            <div
              key={layer.id}
              className={cn(
                'flex items-center gap-3 border-b border-line-faint px-3 py-2.5 text-cell',
                'last:border-b-0',
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <b className="truncate">{layer.name}</b>
                <span className="text-sub text-text-muted">
                  {shapeWords(features.filter((one) => one.layerId === layer.id).length)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => onOpenLayer(layer)}
                className="shrink-0 rounded-tag px-1.5 py-0.5 text-sub hover:bg-hover"
              >
                <AudienceChip
                  audience={layer.audience}
                  divisionIds={layer.divisionIds}
                  divisions={divisions}
                />
              </button>
            </div>
          ))}
          {layers.length === 0 && (
            <p className="m-0 px-3 py-3 text-cell text-text-muted">This map has no layers yet.</p>
          )}
        </div>

        <div className="flex items-end gap-2">
          <SelectInput
            label="Set every layer to"
            value={all}
            onChange={(value) => setAll(value as Audience)}
            options={[
              { value: 'all', label: 'All crews' },
              { value: 'divisions', label: 'Specific divisions…' },
            ]}
          />
          <Button
            variant="primary"
            size="toolbar"
            disabled={busy || layers.length === 0}
            onClick={() => void apply()}
          >
            {busy ? 'Applying…' : 'Apply'}
          </Button>
        </div>

        {scratch > 0 && (
          <p className="m-0 text-cell text-text-muted">
            {scratch === 1 ? 'My scratch stays Only me.' : `${scratch} Only me layers stay as they are.`}
          </p>
        )}
        {said && <p className="m-0 text-cell text-accent-dark" role="status">{said}</p>}

        <p className="m-0 text-sub text-text-faint">
          Changing a row’s chip changes only that layer. Nothing here ever shows in the
          customer portal.
        </p>
      </div>
    </SheetOrDialog>
  )
}

